"use client"
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
} from "@chakra-ui/react"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import { Bars3Icon } from "@heroicons/react/24/outline"

const MotionBox = motion.div
const MotionButton = motion.button

const navLinks = [
    { name: "Home", path: "/" },
    // { name: "Pricing", path: "/pricing" },
    { name: "Blog", path: "/blog" },
    { name: "Job Seeker", path: "https://indiahirings.com" },
]

export default function Navigation({ scrolled }) {
    const { isOpen, onOpen, onClose } = useDisclosure()

    return (
        <>
            <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md" : "bg-transparent shadow-none backdrop-blur-md"}`}>
                <div className="px-6 lg:px-8 xl:px-12 2xl:px-16">
                    <div className="flex items-center justify-between py-2.5">
                        {/* Logo Section */}
                        <div className="flex items-center">
                            <MotionBox initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                                <Link to="/">
                                    <div className="flex items-center">
                                        <h1 className={`text-2xl font-bold transition-colors duration-300 hover:${scrolled ? "text-indigo-500" : "text-blue-100"} ${scrolled ? "text-indigo-600" : "text-white"}`}>
                                            IndiaHirings
                                        </h1>
                                    </div>
                                </Link>
                            </MotionBox>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center space-x-8">
                            {navLinks.map((item, index) => (
                                <MotionBox
                                    key={item.name}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 * index }}
                                    whileHover={{ y: -3 }}
                                    className="relative"
                                >
                                    {item.path.startsWith("http") ? (
                                        <a
                                            href={item.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`font-medium transition-all duration-300 relative ${scrolled ? "text-gray-800 hover:text-indigo-600" : "text-white hover:text-gray-200"}`}
                                        >
                                            <div className="relative group">
                                                {item.name}
                                                <div className={`absolute -bottom-1.5 left-0 w-0 h-0.5 ${scrolled ? "bg-indigo-500" : "bg-white"} transition-all duration-300 group-hover:w-full`}></div>
                                            </div>
                                        </a>
                                    ) : (
                                        <Link
                                            to={item.path}
                                            className={`font-medium transition-all duration-300 relative ${scrolled ? "text-gray-800 hover:text-indigo-600" : "text-white hover:text-gray-200"}`}
                                        >
                                            <div className="relative group">
                                                {item.name}
                                                <div className={`absolute -bottom-1.5 left-0 w-0 h-0.5 ${scrolled ? "bg-indigo-500" : "bg-white"} transition-all duration-300 group-hover:w-full`}></div>
                                            </div>
                                        </Link>
                                    )}
                                </MotionBox>
                            ))}
                        </div>

                        {/* Login/Register Button and Mobile Menu Button */}
                        <div className="flex items-center space-x-4">
                            <MotionButton
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.5 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`hidden md:inline-flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium transition-all duration-300 ${scrolled ? "text-indigo-600 border-indigo-600 hover:bg-indigo-600 hover:text-white" : "text-white border-white hover:bg-white/20"} hover:shadow-md`}
                            >
                                <Link to="/login" className="w-full h-full flex items-center justify-center">
                                    Login / Register
                                </Link>
                            </MotionButton>

                            <button
                                onClick={onOpen}
                                className={`md:hidden p-2 rounded-md ${scrolled ? "text-gray-800 hover:bg-gray-100" : "text-white hover:bg-white/20"}`}
                            >
                                <Bars3Icon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Navigation Drawer */}
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
                <DrawerOverlay backdropFilter="blur(5px)" />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px">
                        <div className="flex items-center">
                            <span className="font-bold text-indigo-600">Indiahirings</span>
                        </div>
                    </DrawerHeader>
                    <DrawerBody>
                        <div className="flex flex-col space-y-4 mt-4">
                            {navLinks.map((item) =>
                                item.path.startsWith("http") ? (
                                    <a
                                        key={item.name}
                                        href={item.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium w-full py-2 transition-all duration-200 hover-link"
                                        onClick={onClose}
                                    >
                                        <MotionBox
                                            whileHover={{ x: 5, color: "rgba(79, 70, 229, 1)" }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {item.name}
                                        </MotionBox>
                                    </a>
                                ) : (
                                    <Link
                                        key={item.name}
                                        to={item.path}
                                        className="font-medium w-full py-2 transition-all duration-200 hover-link"
                                        onClick={onClose}
                                    >
                                        <MotionBox
                                            whileHover={{ x: 5, color: "rgba(79, 70, 229, 1)" }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {item.name}
                                        </MotionBox>
                                    </Link>
                                )
                            )}
                            <div className="border-t my-2"></div>
                            <MotionButton
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-full bg-blue-600 text-white py-2 rounded-md"
                                onClick={onClose}
                            >
                                <Link to="/login" className="w-full block">
                                    Login / Register
                                </Link>
                            </MotionButton>
                        </div>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}